import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { createFHIRPatient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PrivacyPolicySummary from "@/components/PrivacyPolicySummary";
import {
  Heart, User, Users, Mail, Phone, Lock, Eye, EyeOff,
  ArrowRight, ArrowLeft, CheckCircle, Shield, Loader2
} from "lucide-react";

/**
 * Combined Patient/Caregiver Registration
 * Both roles get access to the same unified dashboard
 */
export default function CombinedRegistration() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    role: "caregiver", // patient, caregiver, family
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    relationship: "", // For caregivers: spouse, child, sibling, parent, friend, professional
    patientCode: "", // Optional: link to existing patient
    agreeTerms: false,
    agreeHipaa: false
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.role) newErrors.role = "Please select your role";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 8) newErrors.password = "Password must be at least 8 characters";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (formData.role !== "patient" && !formData.relationship) {
      newErrors.relationship = "Please select your relationship";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.agreeTerms) newErrors.agreeTerms = "You must agree to the terms";
    if (!formData.agreeHipaa) newErrors.agreeHipaa = "You must acknowledge HIPAA practices";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setLoading(true);
    try {
      // Step 1: If registering as patient, create FHIR Patient record (PHI)
      let fhirPatientId = null;
      if (formData.role === "patient") {
        try {
          const fhirPatient = await createFHIRPatient({
            given_name: formData.firstName,
            family_name: formData.lastName,
            birth_date: "1900-01-01", // Will be updated later
            gender: "unknown",
            telephone: formData.phone || null,
            email: formData.email
          });
          fhirPatientId = fhirPatient.id;
        } catch (fhirError) {
          console.error("FHIR patient creation error:", fhirError);
          // Continue with registration even if FHIR fails
        }
      }
      
      // Step 2: Register in MongoDB (non-PHI + FHIR reference)
      const response = await api.post("/caregiver/register", {
        email: formData.email,
        password: formData.password,
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        role: formData.role,
        patient_relationship: formData.relationship || null,
        patient_id: formData.patientCode || null,
        fhir_patient_id: fhirPatientId // Link to FHIR record
      });

      if (response.data.success) {
        const message = fhirPatientId 
          ? "Registration successful! Your PHI is securely stored in our HIPAA-compliant system."
          : "Registration successful! Welcome to Goodwin Hospice.";
        toast.success(message);
        
        // Auto-login after registration
        try {
          const loginRes = await api.post("/auth/login", {
            email: formData.email,
            password: formData.password
          });
          
          if (loginRes.data.token) {
            login(loginRes.data.token, loginRes.data.user);
            navigate("/caregiver-dashboard");
          }
        } catch {
          navigate("/");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const roleDescriptions = {
    patient: "I am receiving hospice care and want to document my journey",
    caregiver: "I am the primary caregiver for someone receiving hospice care",
    family: "I am a family member who wants to stay connected and informed"
  };

  const relationships = [
    { value: "spouse", label: "Spouse/Partner" },
    { value: "child", label: "Adult Child" },
    { value: "parent", label: "Parent" },
    { value: "sibling", label: "Sibling" },
    { value: "friend", label: "Close Friend" },
    { value: "professional", label: "Professional Caregiver" },
    { value: "other", label: "Other" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F4F0] to-white py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#4A7C6F] flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Join Goodwin Hospice</h1>
          <p className="text-slate-600 mt-2">Supporting families with dignity and grace</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                s < step ? "bg-green-500 text-white" :
                s === step ? "bg-[#4A7C6F] text-white" :
                "bg-slate-200 text-slate-500"
              }`}>
                {s < step ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 ${s < step ? "bg-green-500" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        <Card className="border-0 shadow-lg" data-testid="combined-registration">
          {/* Step 1: Account */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle>Create Your Account</CardTitle>
                <CardDescription>Tell us who you are and set up your login</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Role Selection */}
                <div className="space-y-3">
                  <Label>I am a...</Label>
                  <RadioGroup 
                    value={formData.role} 
                    onValueChange={(v) => handleChange("role", v)}
                    className="space-y-3"
                  >
                    {Object.entries(roleDescriptions).map(([role, desc]) => (
                      <div 
                        key={role}
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.role === role 
                            ? "border-[#4A7C6F] bg-[#E8F4F0]" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        onClick={() => handleChange("role", role)}
                      >
                        <RadioGroupItem value={role} id={role} className="mt-1" />
                        <div>
                          <Label htmlFor={role} className="font-medium capitalize cursor-pointer">
                            {role === "caregiver" ? "Family Caregiver" : role}
                          </Label>
                          <p className="text-sm text-slate-500 mt-1">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.role && <p className="text-sm text-red-500">{errors.role}</p>}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10"
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      placeholder="At least 8 characters"
                      className="pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => handleChange("confirmPassword", e.target.value)}
                      placeholder="Confirm your password"
                      className="pl-10"
                    />
                  </div>
                  {errors.confirmPassword && <p className="text-sm text-red-500">{errors.confirmPassword}</p>}
                </div>

                <Button onClick={handleNext} className="w-full bg-[#4A7C6F] hover:bg-[#3D6B5F]">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </>
          )}

          {/* Step 2: Personal Info */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Help us personalize your experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleChange("firstName", e.target.value)}
                      placeholder="First name"
                    />
                    {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                      placeholder="Last name"
                    />
                    {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="(703) 555-0123"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Relationship (for caregivers/family) */}
                {formData.role !== "patient" && (
                  <div className="space-y-2">
                    <Label>Your Relationship to the Patient</Label>
                    <Select 
                      value={formData.relationship} 
                      onValueChange={(v) => handleChange("relationship", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        {relationships.map(rel => (
                          <SelectItem key={rel.value} value={rel.value}>
                            {rel.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.relationship && <p className="text-sm text-red-500">{errors.relationship}</p>}
                  </div>
                )}

                {/* Patient Code (optional) */}
                {formData.role !== "patient" && (
                  <div className="space-y-2">
                    <Label htmlFor="patientCode">Patient Connection Code (Optional)</Label>
                    <Input
                      id="patientCode"
                      value={formData.patientCode}
                      onChange={(e) => handleChange("patientCode", e.target.value)}
                      placeholder="Enter code if provided by hospice"
                    />
                    <p className="text-xs text-slate-500">
                      If you have a code from Goodwin Hospice, enter it to link your account
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleBack} className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button onClick={handleNext} className="flex-1 bg-[#4A7C6F] hover:bg-[#3D6B5F]">
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Agreements */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle>Almost Done!</CardTitle>
                <CardDescription>Review and accept our privacy practices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Privacy Summary */}
                <PrivacyPolicySummary />

                {/* Agreements */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="agreeTerms"
                      checked={formData.agreeTerms}
                      onCheckedChange={(v) => handleChange("agreeTerms", v)}
                      className="mt-1"
                    />
                    <Label htmlFor="agreeTerms" className="text-sm cursor-pointer">
                      I agree to the <a href="#" className="text-[#4A7C6F] underline">Terms of Service</a> and 
                      understand how Goodwin Hospice will use my information.
                    </Label>
                  </div>
                  {errors.agreeTerms && <p className="text-sm text-red-500 -mt-2">{errors.agreeTerms}</p>}

                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="agreeHipaa"
                      checked={formData.agreeHipaa}
                      onCheckedChange={(v) => handleChange("agreeHipaa", v)}
                      className="mt-1"
                    />
                    <Label htmlFor="agreeHipaa" className="text-sm cursor-pointer">
                      I acknowledge the <a href="#" className="text-[#4A7C6F] underline">HIPAA Notice of Privacy Practices</a> and 
                      consent to the collection and use of health information as described.
                    </Label>
                  </div>
                  {errors.agreeHipaa && <p className="text-sm text-red-500 -mt-2">{errors.agreeHipaa}</p>}
                </div>

                {/* What You Get */}
                <div className="p-4 bg-[#E8F4F0] rounded-lg">
                  <h4 className="font-medium text-[#4A7C6F] mb-3">What you&apos;ll get access to:</h4>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Unified caregiver dashboard with patient info
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Daily wellness check-ins and symptom tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      AI-powered grief support and guidance
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Memory & Legacy Journal for life stories
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      24/7 access to hospice resources
                    </li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleBack} className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="flex-1 bg-[#4A7C6F] hover:bg-[#3D6B5F]"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Complete Registration
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        {/* Login Link */}
        <p className="text-center text-slate-600 mt-6">
          Already have an account?{" "}
          <a href="/" className="text-[#4A7C6F] font-medium hover:underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
